import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "./axios"; // Your configured Axios instance
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

// Types
interface Country {
  id: number;
  libelle: string;
}

interface RequestCountry {
  libelle: string;
}

interface CountryQueryParams {
  page?: number;
  per_page?: number;
  search?: string;
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
}

interface PaginatedCountryResponse {
  data: Country[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number;
  to: number;
}

// React Query Keys
export const countryKeys = {
  all: ['countries'] as const,
  list: (page: number, per_page: number, params: Omit<CountryQueryParams, 'page' | 'per_page'>) => 
    ['countries', 'list', page, per_page, params] as const,
  detail: (id: number) => ['countries', 'detail', id] as const,
  country: (id: number) => [...countryKeys.all, id] as const,
};

// Fetch all countries with pagination
export const useGetAllCountries = (params: CountryQueryParams = {}) => {
  const { page = 1, per_page = 10, ...queryParams } = params;
  
  const { data, error, ...res } = useQuery({
    queryKey: countryKeys.list(page, per_page, queryParams),
    queryFn: async (): Promise<PaginatedCountryResponse> => {
      const response = await axios.get('/countries/list', { 
        params: { page, per_page, ...queryParams }
      });
      return response.data;
    },
  });

  return { 
    countries: data?.data ?? [], 
    pagination: data ? {
      current_page: data.current_page,
      last_page: data.last_page,
      per_page: data.per_page,
      total: data.total,
      from: data.from,
      to: data.to,
    } : null,
    ...res 
  };
};
// Get one country by ID
export const useGetCountry = (id: number) => {
  return useQuery<Country>({
    queryKey: countryKeys.country(id),
    queryFn: async () => {
      const response = await axios.get(`/countries/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

// Create a country
export const useCreateCountry = (
  onClose?: () => void,
  initCountry?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (newCountry: RequestCountry) => {
      const response = await axios.post("/countries/add", newCountry);
      return response.data;
    },
    onSuccess: () => {
      onClose && onClose();
      initCountry && initCountry();
      
      // Invalidate all country-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: countryKeys.all });
      
      toast.success(t("country.createSuccess", "Country created successfully"));
    },
    onError: (err: any) => {
      if (err?.response?.data?.code === 401) {
        toast.error(t("country.unauthorized", "You are not authorized to create countries"));
      } else if (err?.response?.data?.code === "23000") {
        toast.error(t("country.alreadyExists", "Country with this name already exists"));
      } else if (err?.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error(t("country.createError", "Error creating country"));
      }
    },
  });
};

// Update a country
export const useUpdateCountry = (
  countryId: number,
  onClose?: () => void,
  initCountry?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updatedCountry: RequestCountry) => {
      const response = await axios.put(`/countries/${countryId}`, updatedCountry);
      return response.data;
    },
    onSuccess: () => {
      onClose && onClose();
      initCountry && initCountry();
      
      // Invalidate both the specific country and all country lists
      queryClient.invalidateQueries({ queryKey: countryKeys.detail(countryId) });
      queryClient.invalidateQueries({ queryKey: countryKeys.all });
      
      toast.success(t("country.updateSuccess", "Country updated successfully"));
    },
    onError: (err: any) => {
      if (err?.response?.data?.code === 401) {
        toast.error(t("country.unauthorizedUpdate", "You are not authorized to update this country"));
      } else if (err?.response?.data?.code === "23000") {
        toast.error(t("country.nameExists", "A country with this name already exists"));
      } else if (err?.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error(t("country.updateError", "Error updating country"));
      }
    },
  });
};

// Delete a country
export const useDeleteCountry = (onSuccess?: () => void) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (countryId: number) => {
      const response = await axios.delete(`/countries/${countryId}`);
      return response.data;
    },
    onSuccess: () => {
      onSuccess && onSuccess();
      
      // Invalidate all country-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: countryKeys.all });
      
      toast.success(t("country.deleteSuccess", "Country deleted successfully"));
    },
    onError: (err: any) => {
      if (err?.response?.data?.code === 401) {
        toast.error(t("country.unauthorizedDelete", "You are not authorized to delete this country"));
      } else if (err?.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error(t("country.deleteError", "Error deleting country"));
      }
    },
  });
};